/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903099954 — useApplePaySession */

import { useRef } from 'react';

import { useAppStore } from '../../../shared/hooks/useAppStore';
import { debug } from '../../../shared/lib/logging';
import { ApplePayPaymentAuthorizedEvent, ApplePayValidateMerchantEvent } from '../../../shared/types';
import { EVENT_NAMES, useSendEventLog } from '../../event-log';
import { validateAppleMerchant } from '../api/payment.api';
import {
    APPLE_PAY_MERCHANT_CAPABILITIES,
    APPLE_PAY_SESSION_VERSION,
    APPLE_PAY_SUPPORTED_NETWORKS,
} from '../payment.constants';
import { SUBUNITS_IN_CURRENCY } from '../../../shared/constants';

interface UseApplePaySessionParams {
    currencyCode: string;
    countryCode: string;
    onPaymentAuthorized: (applePayment: ApplePayPaymentAuthorizedEvent, completePayment: () => void) => void;
}

/** Instance type of `window.ApplePaySession`, kept local to avoid touching the
 * ambient declaration in shared/types.ts. */
type AppleSession = InstanceType<NonNullable<Window['ApplePaySession']>>;

/**
 * The gateway's merchant-validation response must be the raw Apple merchant
 * session object (it carries `merchantSessionIdentifier`). Some gateways wrap
 * it in an envelope; unwrap the common shapes so `completeMerchantValidation`
 * never receives a wrapper (which Safari rejects silently, stranding the
 * session). Returns null when nothing session-shaped is found.
 */
const extractMerchantSession = (data: unknown): Record<string, unknown> | null => {
    const looksLikeSession = (v: unknown): v is Record<string, unknown> =>
        typeof v === 'object' && v !== null && 'merchantSessionIdentifier' in v;

    if (looksLikeSession(data)) return data;
    if (typeof data === 'object' && data !== null) {
        for (const key of ['result', 'session', 'data', 'merchantSession']) {
            const nested = (data as Record<string, unknown>)[key];
            if (looksLikeSession(nested)) return nested;
        }
    }
    return null;
};

export const useApplePaySession = ({
    currencyCode,
    countryCode,
    onPaymentAuthorized,
}: UseApplePaySessionParams) => {
    const order = useAppStore((s) => s.order);
    const { sendEvent } = useSendEventLog();

    // Safari allows one active ApplePaySession per page. A session started with
    // begin() stays active until it completes, is cancelled, or is aborted; a
    // merchant validation that never resolves (or that we feed a bad session
    // object) leaves it dangling, and the next begin() throws
    // `InvalidAccessError: Page already has an active payment session`. Keep a
    // ref to the live session and abort a lingering one before starting a new one.
    const activeSession = useRef<AppleSession | null>(null);

    const clearSession = (session: AppleSession) => {
        if (activeSession.current === session) {
            activeSession.current = null;
        }
    };

    const abortActiveSession = () => {
        if (!activeSession.current) return;
        try {
            activeSession.current.abort();
        } catch (error) {
            debug(`Failed to abort stale Apple Pay session: ${(error as Error)?.message}`, 'warn');
        }
        activeSession.current = null;
    };

    const requestApplePay = () => {
        sendEvent({ name: EVENT_NAMES.paymentInitiated.Apple });

        const paymentRequest = {
            currencyCode,
            countryCode,
            total: {
                label: 'Product',
                // Apple Pay requires the amount as a canonical decimal string
                // ("1.00"), not a number — macOS Safari silently fails to present
                // the sheet on a malformed amount.
                amount: (order?.amount / SUBUNITS_IN_CURRENCY).toFixed(2),
            },
            merchantCapabilities: [...APPLE_PAY_MERCHANT_CAPABILITIES],
            supportedNetworks: [...APPLE_PAY_SUPPORTED_NETWORKS],
        };

        if (!window.ApplePaySession?.canMakePayments()) {
            return;
        }

        // Tear down a session left active by a previous attempt so begin() below
        // cannot throw InvalidAccessError.
        abortActiveSession();

        const session = new window.ApplePaySession!(APPLE_PAY_SESSION_VERSION, paymentRequest);
        activeSession.current = session;

        session.onvalidatemerchant = (event: ApplePayValidateMerchantEvent) => {
            validateAppleMerchant(event.validationURL, order.id)
                .then((response) => {
                    const merchantSession = extractMerchantSession(response);
                    if (!merchantSession) {
                        // A 200 with a non-session body would strand the sheet;
                        // abort so the flow fails cleanly and can be retried.
                        debug(
                            `Apple merchant validation: no session in response, keys=${
                                response && typeof response === 'object'
                                    ? Object.keys(response).join(',')
                                    : typeof response
                            }`,
                            'error',
                        );
                        session.abort();
                        clearSession(session);
                        return;
                    }
                    debug(
                        `Apple merchant validation: session for domain ${String(
                            merchantSession.domainName,
                        )}, merchantIdentifier ${String(merchantSession.merchantIdentifier)}`,
                    );
                    // Safari rejects a session whose domainName is not this page's
                    // host by throwing here, which ends the sheet with "Payment Not
                    // Completed". Catch it so the reason is visible instead of the
                    // rejection landing in the shared catch below.
                    try {
                        session.completeMerchantValidation(merchantSession);
                    } catch (error) {
                        debug(
                            `Apple completeMerchantValidation rejected: ${
                                (error as Error)?.name
                            }: ${(error as Error)?.message} (page host ${
                                window.location.host
                            }, session domain ${String(merchantSession.domainName)})`,
                            'error',
                        );
                        session.abort();
                        clearSession(session);
                    }
                })
                .catch((error) => {
                    // Backend forwards the gateway's non-2xx status, so a failed
                    // validation rejects here. Abort the sheet — without this it
                    // hangs waiting for a completeMerchantValidation that never comes.
                    debug(
                        `Apple merchant validation request failed: ${
                            (error as Error)?.message ?? 'unknown error'
                        }`,
                        'error',
                    );
                    session.abort();
                    clearSession(session);
                });
        };

        session.onpaymentauthorized = (applePayment: ApplePayPaymentAuthorizedEvent) => {
            debug(`Apple payment authorized for order ${order.id}`);
            const completePayment = () => session.completePayment(window.ApplePaySession!.STATUS_SUCCESS);
            onPaymentAuthorized(applePayment, completePayment);
            clearSession(session);
        };

        session.oncancel = function () {
            debug('Payment cancelled', 'warn');
            clearSession(session);
        };

        try {
            session.begin();
        } catch (error) {
            // Should not happen after abortActiveSession(), but never leave a
            // half-created session referenced.
            debug(`Apple Pay begin() failed: ${(error as Error)?.message}`, 'error');
            clearSession(session);
        }
    };

    return { requestApplePay };
};
